import {
	Button,
	HStack,
	Icon,
	IconButton,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Portal,
	Text,
} from '@chakra-ui/react';

import {
	RiPlayLine,
	RiPauseLine,
	RiEditLine,
	RiEyeLine,
	RiCheckLine,
	RiCloseLine,
	RiSendPlaneLine,
	RiCalendarEventLine,
	RiRepeatLine,
	RiFileCopyLine,
	RiMore2Fill,
	RiDeleteBinLine,
} from 'react-icons/ri';

import {
	availableControls,
} from '../detail/controls';

import {
	idOf,
} from '../_lib/campaignState';

import {
	useCampaignUI,
	primaryButton,
	dangerButton,
} from './ui';

const CONTROL = {
	view: {
		label: 'View details',
		icon: RiEyeLine,
	},

	edit: {
		label: 'Continue editing',
		icon: RiEditLine,
	},

	submit: {
		label: 'Submit campaign',
		icon: RiSendPlaneLine,
	},

	approve: {
		label: 'Approve',
		icon: RiCheckLine,
	},

	reject: {
		label: 'Request changes',
		icon: RiCloseLine,
	},

	start: {
		label: 'Launch campaign',
		icon: RiPlayLine,
	},

	pause: {
		label: 'Pause',
		icon: RiPauseLine,
	},

	resume: {
		label: 'Resume',
		icon: RiPlayLine,
	},

	reschedule: {
		label: 'Reschedule',
		icon: RiCalendarEventLine,
	},

	resend: {
		label: 'Build next wave',
		icon: RiRepeatLine,
	},

	duplicate: {
		label: 'Duplicate as draft',
		icon: RiFileCopyLine,
	},

	cancel: {
		label: 'Cancel campaign',
		icon: RiCloseLine,
	},

	delete: {
		label: 'Delete campaign',
		icon: RiDeleteBinLine,
	},
};

export function CampaignActions({
	campaign,
	manager,
	compact = false,
}) {
	const ui = useCampaignUI();

	const id = idOf(campaign);

	const actions =
		availableControls(campaign);

	const busyHere =
		manager.busy?.id === id;

	const verify =
		Boolean(
			manager.verifying[id],
		);

	if (verify) {
		return (
			<Button
				size='sm'
				variant='neutralOutline'
				onClick={() =>
					manager.recheck(
						campaign,
					)
				}
			>
				Check status
			</Button>
		);
	}

	if (
		campaign.state ===
		'PLANNING'
	) {
		return compact ? (
			<Text
				fontSize='xs'
				color={ui.textMuted}
			>
				Preparing...
			</Text>
		) : null;
	}

	/**
	 * Destructive/utility actions never become the main CTA.
	 */
	const first =
		actions.find(
			(key) =>
				![
					'cancel',
					'duplicate',
					'reject',
					'delete',
				].includes(key),
		);

	const primary = compact
		? first ?? 'view'
		: first;

	const secondary =
		actions.filter(
			(key) =>
				key !== primary,
		);

	const drawButton = (key) => (
		<Button
			key={key}
			size='sm'
			leftIcon={
				<Icon
					as={
						CONTROL[key]
							.icon
					}
				/>
			}
			isDisabled={
				manager.isBusy
			}
			isLoading={
				busyHere &&
				manager.busy
					.action === key
			}
			loadingText={
				key === 'start'
					? 'Launching...'
					: 'Working...'
			}
			onClick={(event) => {
				event.stopPropagation();

				manager.request(
					key,
					campaign,
				);
			}}
			{...(
				[
					'start',
					'resume',
					'approve',
					'submit',
				].includes(key)
					? primaryButton(ui)
					: [
							'cancel',
							'reject',
							'delete',
						].includes(key)
						? dangerButton(ui)
						: {
								variant:
									'neutralOutline',
							}
			)}
		>
			{CONTROL[key].label}
		</Button>
	);

	return (
		<HStack
			spacing={2}
			flexWrap='wrap'
			rowGap={2}
		>
			{primary &&
				drawButton(
					primary,
				)}

			{!compact &&
				secondary
					.filter((key) =>
						[
							'reschedule',
							'reject',
						].includes(
							key,
						),
					)
					.map(
						drawButton,
					)}

			<Menu
				isLazy
				placement='bottom-end'
			>
				<MenuButton
					as={IconButton}
					icon={
						<RiMore2Fill />
					}
					aria-label={`Actions for ${campaign.name || 'campaign'}`}
					variant='ghost'
					size='sm'
					color={
						ui.textSecondary
					}
					isDisabled={
						manager.isBusy
					}
					onClick={(
						event,
					) =>
						event.stopPropagation()
					}
				/>

				<Portal>
					<MenuList zIndex='popover'>
						{(
							compact &&
							primary !==
								'view'
								? [
										'view',
										...secondary,
									]
								: secondary.filter(
										(
											key,
										) =>
											compact ||
											![
												'reschedule',
												'reject',
											].includes(
												key,
											),
									)
						).map(
							(key) => (
								<MenuItem
									key={
										key
									}
									icon={
										<Icon
											as={
												CONTROL[
													key
												]
													.icon
											}
										/>
									}
									color={
										[
											'cancel',
											'delete',
										].includes(
											key,
										)
											? ui.danger
											: ui.textPrimary
									}
									onClick={(
										event,
									) => {
										event.stopPropagation();

										manager.request(
											key,
											campaign,
										);
									}}
								>
									{
										CONTROL[
											key
										]
											.label
									}
								</MenuItem>
							),
						)}

						{!actions.length && (
							<MenuItem
								icon={
									<RiEyeLine />
								}
								onClick={() =>
									manager.request(
										'view',
										campaign,
									)
								}
							>
								View details
							</MenuItem>
						)}
					</MenuList>
				</Portal>
			</Menu>
		</HStack>
	);
}